import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Compass, Send, ShieldCheck, ShieldAlert, Loader2, HeartHandshake, Sparkles,
  ChevronDown, ClipboardList, CheckCircle2, HelpCircle, Mic, MicOff, BarChart3, Phone, MessageSquare,
} from "lucide-react";
import { PlanComparePanel } from "@/components/PlanComparePanel";
import { DecisionCase } from "@/components/case/DecisionCase";
import { useAdvisorLine } from "@/lib/useAdvisorLine";
import { isPlanShopping } from "@/lib/planIntent";
import { useContactPrefill, ConsentOnFile } from "@/lib/useContactPrefill";

const STARTERS = [
  { label: "Help me choose a 2027 plan", action: "finder" },
  { label: "What's changing in my plan for 2027?" },
  { label: "Will my doctors and prescriptions be covered?" },
  { label: "Are my Part B reduction or grocery benefits changing?" },
];

const ACTION_LABELS = {
  show_csnp: "Show C-SNP plans",
  compare_current: "Compare with my current coverage",
  check_prescriptions: "Check my prescriptions",
  check_doctors: "Check my doctors",
  request_advisor: "Continue with a licensed insurance agent",
};
const ACTION_PROMPTS = {
  show_csnp: "Are any of those C-SNP plans?",
  compare_current: "Compare these with my current coverage.",
  check_prescriptions: "Check my prescriptions.",
  check_doctors: "Is my doctor in network with these?",
};

export default function MemberAskNauticus() {
  const [ctx, setCtx] = useState(null);
  const [token, setToken] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [advisorReason, setAdvisorReason] = useState(null);
  const [recap, setRecap] = useState(null);
  const [recapOpen, setRecapOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceErr, setVoiceErr] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [finderOpen, setFinderOpen] = useState(false);
  const [finderSeed, setFinderSeed] = useState(null);
  const line = useAdvisorLine();
  const comms = line ? { available: true, business_number: line.phone, text_available: line.smsReady } : null;
  const lineLabel = line?.assigned ? "my agent" : line?.label;
  const [confirmedCurrentCard, setConfirmedCurrentCard] = useState(null);
  const [compareEligible, setCompareEligible] = useState(false);
  const [replaceAsk, setReplaceAsk] = useState(null);
  const [claimAsk, setClaimAsk] = useState(null);
  const endRef = useRef(null);
  const recognitionRef = useRef(null);
  const baseRef = useRef("");
  const listeningRef = useRef(false);

  const voiceSupported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const voiceEnabled = voiceSupported;

  // Continuity: claim an anonymous session if one is stored, else resume latest / start fresh.
  useEffect(() => {
    const wantCompare = new URLSearchParams(window.location.search).get("compare") === "1";
    (async () => {
      let ctxData = null;
      try {
        const c = await api.get("/consumer/ask/context");
        ctxData = c.data;
        setCtx(c.data);
        if (c.data?.pending_current_plan) setReplaceAsk({ pl: c.data.pending_current_plan, existing: c.data.coverage });
      } catch { /* ignore */ }
      const openCompare = () => { if (wantCompare) setCompareOpen(true); };
      const anon = localStorage.getItem("ask_session_token");
      const intended = anon && localStorage.getItem("ask_save_intent") === anon;
      try {
        if (anon && intended) {
          await claimAnon(anon);
        } else if (ctxData?.uncarried_ask_session) {
          await claimAnon(ctxData.uncarried_ask_session);
        } else {
          if (anon) setClaimAsk(anon);
          await resumeLatest();
        }
      } catch (e) { /* ignore */ }
      openCompare();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claimAnon = async (anon) => {
    const r = await api.post("/consumer/ask/claim", { session_token: anon });
    try { localStorage.removeItem("ask_session_token"); localStorage.removeItem("ask_save_intent"); } catch { /* ignore */ }
    setToken(r.data.session_token);
    await loadMessages(r.data.session_token);
    try {
      const c = await api.get("/consumer/ask/context");
      setCtx(c.data);
      if (c.data?.pending_current_plan) setReplaceAsk({ pl: c.data.pending_current_plan, existing: c.data.coverage });
    } catch { /* ignore */ }
  };

  const resumeLatest = async () => {
    const list = await api.get("/consumer/ask/sessions");
    if (list.data.sessions?.length) {
      const t = list.data.sessions[0].session_token;
      setToken(t);
      await loadMessages(t);
    } else {
      const s = await api.post("/consumer/ask/start");
      setToken(s.data.session_token);
    }
  };

  const answerClaim = async (yes) => {
    const anon = claimAsk;
    setClaimAsk(null);
    if (yes) { try { await claimAnon(anon); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } return; }
    try { localStorage.removeItem("ask_session_token"); localStorage.removeItem("ask_save_intent"); } catch { /* ignore */ }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);
  // Stop any active dictation when leaving the page.
  useEffect(() => () => { try { recognitionRef.current?.abort(); } catch { /* ignore */ } }, []);

  const loadMessages = async (t) => {
    try {
      const r = await api.get(`/consumer/ask/${t}`);
      setMessages(r.data.messages || []);
    } catch { /* ignore */ }
  };

  const startNew = async () => {
    const s = await api.post("/consumer/ask/start");
    setToken(s.data.session_token);
    setMessages([]);
    setConfirmedCurrentCard(null);
    setRecap(null);
  };

  // --- Voice dictation (browser Web Speech API; no server, no stored audio) ---
  const toggleDictation = () => {
    if (!voiceSupported) {
      setVoiceErr("Voice typing isn't supported in this browser. Try Chrome, Edge, or Safari — or just type your question.");
      return;
    }
    if (listening) { listeningRef.current = false; try { recognitionRef.current?.stop(); } catch { /* ignore */ } return; }
    setVoiceErr(null);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    baseRef.current = input ? input.trim() + " " : "";
    rec.onresult = (e) => {
      if (!listeningRef.current) return; // ignore late events after Send/stop
      let finalText = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t; else interim += t;
      }
      setInput((baseRef.current + finalText + interim).replace(/\s+/g, " ").replace(/^\s/, ""));
      if (finalText) baseRef.current = (baseRef.current + finalText).replace(/\s+/g, " ");
    };
    rec.onerror = (e) => {
      listeningRef.current = false; setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed")
        setVoiceErr("Microphone access was blocked. Allow mic access in your browser, or type your question instead.");
      else if (e.error === "no-speech")
        setVoiceErr("I didn't catch that. Tap the mic to try again, or type your question.");
      else if (e.error === "audio-capture")
        setVoiceErr("No microphone was found. You can type your question instead.");
      else setVoiceErr("Voice typing stopped. You can type your question.");
    };
    rec.onend = () => { setListening(false); listeningRef.current = false; };
    recognitionRef.current = rec;
    try { rec.start(); listeningRef.current = true; setListening(true); }
    catch { listeningRef.current = false; setListening(false); setVoiceErr("Couldn't start voice typing. You can type your question."); }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || !token || sending) return;
    if (isPlanShopping(content)) {
      listeningRef.current = false; setListening(false);
      try { recognitionRef.current?.abort(); } catch { /* ignore */ }
      setInput(""); setFinderSeed(content); setFinderOpen(true);
      return;
    }
    // End dictation before clearing input so late recognition events can't repopulate it.
    listeningRef.current = false; setListening(false);
    try { recognitionRef.current?.abort(); } catch { /* ignore */ }
    setInput("");
    setMessages((p) => [...p, { role: "user", content }]);
    setSending(true);
    try {
      const { data } = await api.post(`/consumer/ask/${token}/message`, { content });
      setMessages((p) => [...p, {
        role: "assistant", content: data.reply, sources: data.sources || [],
        answer_state: data.answer_state, what_we_relied_on: data.what_we_relied_on,
        plans: data.plans || null, next_actions: data.next_actions || null,
      }]);
      if (["coverage_unverified", "coverage_ambiguous", "plan_search", "need_county", "plan_identity_resolved"].includes(data.answer_state)) {
        api.get("/consumer/ask/context").then((r) => setCtx(r.data)).catch(() => {});
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSending(false); }
  };

  const doRecap = async () => {
    if (!token) return;
    try {
      const r = await api.post(`/consumer/ask/${token}/recap`);
      setRecap(r.data); setRecapOpen(true);
    } catch (e) { toast.error("Couldn't build a recap yet."); }
  };

  const cov = ctx?.coverage || {};
  const first = ctx?.first_name;
  const candidateIds = (ctx?.candidate_plans || []).map((p) => p.display_id);

  // Eligibility prefill for the one-tap "Compare with 2027 plans I qualify for" button: reuse the
  // member's already-reported Medicaid status + chronic conditions (never re-asked). Condition values
  // are mapped back to the guided-flow keys the eligible-plans endpoint expects.
  const CONDITION_KEY = { "diabetes": "diabetes", "cardiovascular disorders": "heart", "congestive heart failure": "heart", "chronic lung disorders": "lung", "COPD": "lung" };
  const _medObj = ctx?.reported_facts?.medicaid;
  const _medStatus = _medObj ? (_medObj.status || (_medObj.has_medicaid ? "yes" : "no")) : "";
  const _eligConds = [...new Set((ctx?.reported_facts?.conditions || []).map((c) => CONDITION_KEY[c.value]).filter(Boolean))];
  const eligibilityPrefill = {
    medicaid: _medStatus, conditions: _eligConds,
    autoLoad: !!(ctx?.residence?.county && (_medStatus === "yes" || _medStatus === "no" || _eligConds.length > 0)),
  };

  const refreshCtx = async () => {
    try { const c = await api.get("/consumer/ask/context"); setCtx(c.data); } catch { /* ignore */ }
  };

  const addToReview = async (pl) => {
    if (!token || !pl?.contract_id) return;
    try {
      const { data } = await api.post(`/consumer/ask/${token}/candidates`, {
        contract_id: pl.contract_id, pbp: pl.pbp, segment_id: pl.segment_id,
      });
      if (data.already_current) { toast.info(data.message); await refreshCtx(); return; }
      toast.success(`Added to your review: ${data.candidate.plan_name}`);
      await refreshCtx();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const confirmCurrentPlan = async (pl, replace = false) => {
    if (!token || !pl?.contract_id) return;
    try {
      await api.post(`/consumer/ask/${token}/current-plan`, {
        contract_id: pl.contract_id, pbp: pl.pbp, segment_id: pl.segment_id, replace,
      });
      setReplaceAsk(null);
      toast.success(`Saved your current plan: ${pl.plan_name}`);
      await refreshCtx();
      // Immediately open the compare experience with the just-confirmed current plan prefilled so
      // the member can see it alongside 2027 options without re-identifying it.
      setConfirmedCurrentCard({
        contract_id: pl.contract_id, pbp: pl.pbp, segment_id: pl.segment_id,
        display_id: pl.display_id, plan_name: pl.plan_name, organization: pl.organization,
        plan_type: pl.plan_type, snp_type: pl.snp_type,
      });
      setMessages((p) => [...p, { role: "assistant", answer_state: "current_plan_saved",
        content: `Saved — ${pl.plan_name} is now your current coverage on file. It's confirmed by you; your enrollment eligibility isn't independently verified here. I've opened a comparison so you can see it alongside 2027 options.` }]);
      setCompareOpen(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "current_plan_conflict") {
        if (d.replaceable) { setReplaceAsk({ pl, existing: d.existing }); return; }
        setReplaceAsk({ pl, existing: d.existing, locked: true });
        return;
      }
      toast.error(formatApiError(d));
    }
  };

  const keepSavedPlan = async () => {
    setReplaceAsk(null);
    try { await api.post("/consumer/ask/pending-current/dismiss"); } catch { /* ignore */ }
  };

  const requestCorrection = async () => {
    const pl = replaceAsk?.pl || {};
    setReplaceAsk(null);
    try {
      await api.post("/consumer/ask/coverage-correction", { contract_id: pl.contract_id, pbp: pl.pbp, segment_id: pl.segment_id, plan_name: pl.plan_name });
      toast.success("We've asked your licensed insurance agent to check and correct your plan on file.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // One-tap: open the compare dialog in eligibility-filtered mode (uses the member's known
  // Medicaid + conditions to auto-load the 2027 plans they qualify for), current plan prefilled.
  const openEligibleCompare = () => { setCompareEligible(true); setCompareOpen(true); };

  // Self-enrollment click-through (routes to the owner's SunFire marketplace PURL; link_opened only).
  const handleSelfEnroll = async (cands) => {
    if (!token) return;
    const first = (cands && cands[0]) || null;
    try {
      const { data } = await api.post(`/consumer/ask/${token}/self-enroll`,
        first ? { contract_id: first.contract_id, pbp: first.pbp, segment_id: first.segment_id, plan_name: first.plan_name } : {});
      window.open(data.url, "_blank", "noopener");
      toast.success("Sunfire opened in a new window. You'll need to enter your information again there.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // Save the compared 2027 picks to the review, then hand off to a licensed insurance agent with that context.
  const handleSaveCompareAdvisor = async (cands, _current) => {
    if (!token) return;
    const picks = (cands || []).filter((c) => c?.contract_id);
    let saved = 0, lastErr = null;
    for (const c of picks) {
      try {
        await api.post(`/consumer/ask/${token}/candidates`,
          { contract_id: c.contract_id, pbp: c.pbp, segment_id: c.segment_id, plan_year: 2027 });
        saved += 1;
      } catch (e) {
        if (e?.response?.status === 409) saved += 1; // already in review — fine
        else lastErr = e;
      }
    }
    await refreshCtx();
    if (picks.length > 0 && saved === 0) {
      toast.error(formatApiError(lastErr?.response?.data?.detail) || "We couldn't save those plans to your review. Please try again.");
      return; // keep the compare dialog open so the member can retry
    }
    if (saved > 0) toast.success("Saved your comparison to your review.");
    setCompareOpen(false);
    setAdvisorOpen(true);
  };

  const runCompare = async () => {
    if (!token) return;
    try {
      // Shared year-aware comparison engine (same service Helm agent uses): current 2026 coverage vs
      // the 2027 versions of the plans under review. Supported benefit fields only; unknowns stay explicit.
      const cands = (ctx?.candidate_plans || []).map((p) => {
        const [contract_id, pbp, segment_id] = String(p.display_id || "").split("-");
        return { contract_id, pbp, segment_id };
      });
      if (cands.length === 0) {
        setMessages((p) => [...p, { role: "assistant", answer_state: "no_candidates",
          content: "Add at least one plan to your review and I can compare it to your current coverage." }]);
        return;
      }
      const { data } = await api.post(`/consumer/ask/${token}/compare-year`,
        { candidates: cands, year_candidate: 2027 });
      if (!data.ok || !data.current) {
        setMessages((p) => [...p, { role: "assistant", answer_state: "no_current_coverage",
          content: "Confirm your current plan first, then I can compare it to the plans you're reviewing." }]);
        return;
      }
      // Adapt the shared-service response to the existing comparison render shape (no JSX change).
      const adapted = {
        current: { plan_name: data.current.plan_name || "Your current coverage" },
        candidates: data.candidates.map((b) => ({
          display_id: b.display_id, plan_name: b.plan_name || b.display_id,
          subtitle: b.county_eligible === false ? "not offered in your county"
            : (b.from_preview_staging ? "2027 (preview data)" : null),
        })),
        rows: data.rows.map((r) => ({
          key: r.key, label: r.label, available: true,
          current: r.current?.text || "Not available",
          candidates: r.candidates.map((c) => ({ display_id: c.display_id, value: c.text })),
        })),
        consequences: (data.limits?.fields_with_unknown_values?.length
          ? [{ text: `Not yet verified for 2027: ${data.limits.fields_with_unknown_values.slice(0, 4).join(", ")}. Doctor and medication checks are shown separately in the comparison.` }]
          : []),
      };
      setMessages((p) => [...p, { role: "assistant", answer_state: "comparison", comparison: adapted,
        content: `Comparing ${adapted.current.plan_name} with ${adapted.candidates.length} plan(s) you're reviewing (2027):` }]);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] text-[#0B192C]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Compass className="w-6 h-6 text-[#D4AF37]" />
            <div>
              <h1 className="font-display text-xl font-bold" data-testid="consumer-ask-title">
                Ask Nauticus{first ? `, ${first}` : ""}
              </h1>
              <p className="text-[#5B6670] text-xs">Your Medicare questions, answered from verified facts in My Nauticus.</p>
              {ctx?.residence?.county && (
                <span data-testid="consumer-residence-county" className="mt-1 inline-block text-[11px] text-[#5B6670] bg-[#0B192C]/5 rounded-full px-2 py-0.5">
                  {ctx.residence.county} County{ctx.residence.provisional ? " · provisional" : ""}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {comms?.available && <a href={`tel:${comms.business_number}`} className="inline-flex items-center rounded-full border border-[#0B192C] px-3 h-8 text-xs font-semibold text-[#0B192C]" data-testid="consumer-call-agent"><Phone className="w-3.5 h-3.5 mr-1" />Call {lineLabel}</a>}
            {comms?.text_available && <a href={`sms:${comms.business_number}`} className="inline-flex items-center rounded-full border border-[#0B192C] px-3 h-8 text-xs font-semibold text-[#0B192C]" data-testid="consumer-text-agent"><MessageSquare className="w-3.5 h-3.5 mr-1" />Text {lineLabel}</a>}
            <Button onClick={() => setFinderOpen(true)} size="sm" className="rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62]" data-testid="consumer-open-compare"><BarChart3 className="w-3.5 h-3.5 mr-1" />Find &amp; compare 2027 plans</Button>
            <Button onClick={startNew} variant="outline" size="sm" className="rounded-full border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C]/10" data-testid="consumer-ask-new">New chat</Button>
          </div>
        </div>

        {/* Coverage state banner */}
        <div className="mt-4">
          {!ctx ? (
            <div className="rounded-xl bg-white border border-[#E7E4DB] px-4 py-3 flex items-center gap-2" data-testid="consumer-cov-loading">
              <Loader2 className="w-4 h-4 animate-spin text-[#8A8F98]" />
              <span className="text-sm text-[#5B6670]">Loading your coverage…</span>
            </div>
          ) : cov.state === "verified" ? (
            <div className="rounded-xl bg-[#ECFDF5] border border-[#A7F3D0] px-4 py-3 flex items-start gap-2" data-testid="consumer-cov-verified">
              <ShieldCheck className="w-4 h-4 text-[#059669] mt-0.5" />
              <span className="text-sm text-[#065F46]">Current plan (confirmed by you): <b>{cov.plan_name}</b> · {cov.carrier} · {cov.display_id} · {cov.plan_year}<span className="block text-[#5B6670] text-[11px] mt-0.5">Confirmed by you — enrollment eligibility isn't independently verified here.</span>{cov.advisor_verified?.present && <span className="block text-[#0B192C] text-[11px] mt-1" data-testid="consumer-advisor-verified">{cov.advisor_verified.attribution}</span>}</span>
            </div>
          ) : cov.state === "ambiguous" ? (
            <div className="rounded-xl bg-[#FFF7ED] border border-[#FDBA74] px-4 py-3 flex items-center gap-2" data-testid="consumer-cov-ambiguous">
              <ShieldAlert className="w-4 h-4 text-[#B45309]" />
              <span className="text-sm text-[#9A3412]">My Nauticus has more than one current plan on file — it needs to be confirmed before I can answer plan-specific questions.</span>
            </div>
          ) : (
            <div className="rounded-xl bg-white border border-[#E7E4DB] px-4 py-3 flex items-center gap-2" data-testid="consumer-cov-unverified">
              <ShieldAlert className="w-4 h-4 text-[#8A8F98]" />
              <span className="text-sm text-[#5B6670]">No verified current plan yet — I can answer general questions, and be specific once your plan is confirmed.</span>
            </div>
          )}
        </div>

        {/* Active Medicare Review (Slice 1): acknowledge the saved, ongoing review + preserved reason */}
        {ctx?.active_review && (
          <div className="mt-3 rounded-xl bg-[#0B192C]/5 border border-[#0B192C]/30 px-4 py-3" data-testid="consumer-active-review">
            <p className="text-sm font-semibold text-[#0B192C]">Your Medicare review is saved{first ? `, ${first}` : ""}.</p>
            {ctx.active_review.reason && (
              <p className="text-sm text-[#374151] mt-0.5" data-testid="consumer-active-review-reason">Reviewing: {ctx.active_review.reason}</p>
            )}
            {ctx.active_review.objective && ctx.active_review.objective !== ctx.active_review.reason && (
              <p className="text-sm text-[#5B6670] mt-0.5" data-testid="consumer-active-review-objective">Now working on: {ctx.active_review.objective}</p>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-5 space-y-4" data-testid="consumer-ask-thread">
          {messages.length === 0 && !sending && (
            <div className="space-y-3">
              <p className="text-[#5B6670] text-sm">Try asking:</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {STARTERS.map((s) => (
                  <button key={s.label} onClick={() => s.action === "finder" ? setFinderOpen(true) : send(s.label)} className={`text-left text-base rounded-xl px-4 py-4 shadow-sm transition-colors border ${s.action ? "bg-[#0B192C] text-white border-[#0B192C] hover:bg-[#1E3E62] font-semibold" : "bg-white text-[#0B192C] border-[#E7E4DB] hover:bg-[#0B192C]/5 hover:border-[#0B192C]/40"}`} data-testid="consumer-ask-starter">{s.action ? <span className="inline-flex items-center gap-1.5"><BarChart3 className="w-4 h-4" /> {s.label}</span> : s.label}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[90%] rounded-2xl px-4 py-3.5 text-base shadow-sm ${m.role === "user" ? "bg-[#0B192C] text-white font-medium" : "bg-white border border-[#E7E4DB] text-[#0B192C]"}`} data-testid={`consumer-msg-${m.role}`}>
                <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                {m.role === "assistant" && m.plans?.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="consumer-plan-results">
                    {m.plans.map((pl, k) => (
                      <div key={k} className="rounded-xl bg-[#F7F6F2] border border-[#E7E4DB] px-3 py-2.5" data-testid="consumer-plan-card">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-[#0B192C]">{pl.plan_name}</p>
                            <p className="text-[11px] text-[#8A8F98]">{pl.organization}{pl.display_id ? ` · ${pl.display_id}` : ""}</p>
                          </div>
                          {pl.is_csnp && <span className="text-[10px] font-semibold text-[#0B192C] border border-[#0B192C]/50 rounded-full px-2 py-0.5 shrink-0" data-testid="consumer-plan-csnp">C-SNP</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-[11px] text-[#5B6670]">
                          {pl.plan_type && <span>{pl.plan_type}</span>}
                          {pl.monthly_premium != null && <span>Premium: {String(pl.monthly_premium)}</span>}
                          {pl.moop != null && <span>MOOP: {String(pl.moop)}</span>}
                          {pl.includes_drug_coverage && <span>Includes drug coverage</span>}
                          {pl.snp_type && !pl.is_csnp && <span>SNP: {pl.snp_type}</span>}
                        </div>
                        {pl.contract_id && (
                          m.answer_state === "plan_identity_resolved" ? (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid="consumer-identity-choices">
                              <button onClick={() => confirmCurrentPlan(pl)} disabled={sending}
                                className="text-[11px] bg-[#059669] text-white hover:bg-[#047857] rounded-full px-3 py-1 font-semibold disabled:opacity-50"
                                data-testid="consumer-identity-current">This is my current plan</button>
                              {candidateIds.includes(pl.display_id) ? (
                                <span className="flex items-center gap-1 text-[11px] text-[#059669]" data-testid="consumer-plan-added">
                                  <CheckCircle2 className="w-3 h-3" /> In your review
                                </span>
                              ) : (
                                <button onClick={() => addToReview(pl)} disabled={sending}
                                  className="text-[11px] bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full px-3 py-1 font-semibold disabled:opacity-50"
                                  data-testid="consumer-identity-add">Add to my review</button>
                              )}
                            </div>
                          ) : (m.answer_state === "current_plan_confirm" || m.answer_state === "current_plan_choices") ? (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid="consumer-current-plan-choices">
                              <button onClick={() => confirmCurrentPlan(pl)} disabled={sending}
                                className="text-[11px] bg-[#059669] text-white hover:bg-[#047857] rounded-full px-3 py-1 font-semibold disabled:opacity-50"
                                data-testid="consumer-current-plan-confirm">This is my current plan</button>
                              {candidateIds.includes(pl.display_id) ? (
                                <span className="flex items-center gap-1 text-[11px] text-[#059669]" data-testid="consumer-plan-added">
                                  <CheckCircle2 className="w-3 h-3" /> In your review
                                </span>
                              ) : (
                                <button onClick={() => addToReview(pl)} disabled={sending}
                                  className="text-[11px] bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full px-3 py-1 font-semibold disabled:opacity-50"
                                  data-testid="consumer-current-plan-add">Compare / review this plan</button>
                              )}
                            </div>
                          ) : candidateIds.includes(pl.display_id) ? (
                            <div className="mt-2 flex items-center gap-1 text-[11px] text-[#059669]" data-testid="consumer-plan-added">
                              <CheckCircle2 className="w-3 h-3" /> In your review
                            </div>
                          ) : (
                            <button onClick={() => addToReview(pl)} disabled={sending}
                              className="mt-2 text-[11px] bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full px-3 py-1 font-semibold disabled:opacity-50"
                              data-testid="consumer-plan-add">Add to my review</button>
                          )
                        )}
                      </div>
                    ))}
                    <p className="text-[10px] text-[#8A8F98]">Factual plan data from Nauticus — not a ranking or recommendation.</p>
                  </div>
                )}
                {m.role === "assistant" && m.comparison && (
                  <div className="mt-3 rounded-xl bg-[#F7F6F2] border border-[#E7E4DB] p-3" data-testid="consumer-comparison">
                    {m.comparison.rows.filter((r) => r.available).map((r) => (
                      <div key={r.key} className="py-1.5 border-t border-[#E7E4DB] text-[11px]" data-testid="comparison-row" data-benefit={r.key}>
                        <div className="text-[#5B6670] mb-0.5">{r.label}</div>
                        <div className="pl-2 space-y-0.5">
                          <div className="flex justify-between gap-3">
                            <span className="text-[#8A8F98] shrink-0">Current</span>
                            <span className="text-[#0B192C] text-right" data-testid="comparison-current">{r.current || "Not available in current data"}</span>
                          </div>
                          {r.candidates.map((cv) => {
                            const cand = m.comparison.candidates.find((c) => c.display_id === cv.display_id);
                            return (
                            <div key={cv.display_id} className="flex justify-between gap-3">
                              <span className="text-[#8A8F98] truncate" data-testid="comparison-candidate-label">
                                {cand?.plan_name || cv.display_id}
                                {cand?.subtitle ? <span className="text-[#B4B8BF]"> — {cand.subtitle}</span> : ""}
                              </span>
                              <span className="text-[#0B192C] text-right" data-testid="comparison-candidate">{cv.value || "Not available in current data"}</span>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {(m.comparison.consequences || []).map((c, ci) => (
                      <p key={ci} className="mt-2 text-[11px] text-[#0B192C]" data-testid="comparison-consequence">{c.text}</p>
                    ))}
                    <p className="mt-2 text-[10px] text-[#8A8F98]">Factual plan data, not a ranking or recommendation. Current coverage is confirmed by you; enrollment eligibility is not independently verified.</p>
                  </div>
                )}
                {m.role === "assistant" && m.answer_state === "current_plan_saved" && (
                  <div className="mt-3" data-testid="consumer-eligible-compare-cta">
                    <button onClick={openEligibleCompare} disabled={sending}
                      className="inline-flex items-center gap-1.5 text-[11px] bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full px-3 py-1.5 font-semibold disabled:opacity-50"
                      data-testid="consumer-compare-eligible-btn">
                      <BarChart3 className="w-3.5 h-3.5" /> Compare with 2027 plans I qualify for
                    </button>
                  </div>
                )}
                {m.role === "assistant" && m.next_actions?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="consumer-next-actions">
                    {m.next_actions.map((a) => (
                      <button key={a} onClick={() => a === "request_advisor" ? setAdvisorOpen(true) : a === "compare_current" ? runCompare() : send(ACTION_PROMPTS[a] || a)} disabled={sending}
                        className="text-[11px] bg-[#0B192C]/5 hover:bg-[#0B192C]/10 text-[#0B192C] rounded-full px-3 py-1.5 disabled:opacity-50"
                        data-testid={`consumer-next-action-${a}`}>{ACTION_LABELS[a] || a}</button>
                    ))}
                  </div>
                )}
                {m.role === "assistant" && (m.sources?.length > 0 || m.what_we_relied_on) && (
                  <details className="mt-2" data-testid="consumer-provenance">
                    <summary className="cursor-pointer text-[11px] text-[#5B6670] flex items-center gap-1"><ChevronDown className="w-3 h-3" />What Nauticus relied on</summary>
                    <div className="mt-1 text-[11px] text-[#5B6670] space-y-0.5">
                      {(m.sources || []).map((s) => (
                        <div key={s.url} data-testid="consumer-source-ref" data-source-url={s.url} className="block text-[#5B6670]">{s.label}</div>
                      ))}
                      {m.what_we_relied_on && (
                        <p>CMS-sourced plan facts · {m.what_we_relied_on.contract_id}-{m.what_we_relied_on.pbp}-{m.what_we_relied_on.segment_id} · {m.what_we_relied_on.plan_year}{m.what_we_relied_on.source_vintage ? ` · ${m.what_we_relied_on.source_vintage}` : ""}</p>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="bg-white border border-[#E7E4DB] rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-[#0B192C]" /></div></div>}
          <div ref={endRef} />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-3">
          {messages.length > 0 && <Button onClick={() => setFinderOpen(true)} size="sm" className="rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62]" data-testid="consumer-open-finder"><BarChart3 className="w-4 h-4 mr-1.5" />My 2027 plan search</Button>}
          <Button onClick={doRecap} variant="outline" size="sm" className="rounded-full border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C]/10" data-testid="consumer-ask-recap"><ClipboardList className="w-4 h-4 mr-1.5" />Recap</Button>
          <Button onClick={() => setAdvisorOpen(true)} variant="outline" size="sm" className="rounded-full border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C]/10" data-testid="consumer-ask-advisor"><HeartHandshake className="w-4 h-4 mr-1.5" />Talk to a licensed insurance agent</Button>
        </div>

        {/* Composer */}
        <div className="sticky bottom-0 bg-[#F7F6F2] pb-5 pt-2">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <div className="flex-1 relative">
              <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your plan or Medicare in general…"
                className="bg-white border-[#D7D4CB] text-[#0B192C] placeholder:text-[#8A8F98] h-12 rounded-full pl-5 pr-12 shadow-sm" data-testid="consumer-ask-input" disabled={!token} />
              {voiceEnabled && (
              <button type="button" onClick={toggleDictation} disabled={!token}
                aria-label={listening ? "Stop voice typing" : "Start voice typing"} data-testid="consumer-ask-mic"
                className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${listening ? "bg-[#0B192C] text-white animate-pulse" : "text-[#5B6670] hover:bg-[#0B192C]/5"}`}>
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              )}
            </div>
            <Button type="submit" disabled={!token || sending || !input.trim()} className={`rounded-full h-12 w-12 p-0 shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1E3E62] ${(!token || sending || !input.trim()) ? "bg-[#E7E4DB] text-[#AFABA0] cursor-not-allowed hover:bg-[#E7E4DB]" : "bg-[#D4AF37] text-[#0B192C] hover:bg-[#C8A233] active:bg-[#B8941F]"}`} data-testid="consumer-ask-send"><Send className="w-5 h-5" /></Button>
          </form>
          {listening && (
            <p className="text-[#0B192C] text-[11px] mt-2 text-center flex items-center justify-center gap-1.5" data-testid="consumer-ask-listening">
              <span className="w-2 h-2 rounded-full bg-[#0B192C] animate-pulse" /> Listening… speak your question, then review it before sending.
            </p>
          )}
          {voiceErr && <p className="text-[#B45309] text-[11px] mt-2 text-center" data-testid="consumer-ask-voice-error">{voiceErr}</p>}
          {voiceEnabled && (
            <p className="text-[#8A8F98] text-[10px] mt-1.5 text-center" data-testid="consumer-ask-voice-notice">
              Voice typing uses your browser's speech service, which may process audio externally. You can always type instead.
            </p>
          )}
          <p className="text-[#8A8F98] text-[11px] mt-2 text-center">Nauticus answers plan questions only from your verified plan data, and never guesses. Not insurance advice.</p>
        </div>
      </div>

      <RecapDialog open={recapOpen} onOpenChange={setRecapOpen} recap={recap} onAdvisor={() => { setRecapOpen(false); setAdvisorOpen(true); }} />
      <ReplaceCurrentDialog ask={replaceAsk} onReplace={() => confirmCurrentPlan(replaceAsk.pl, true)} onKeep={keepSavedPlan} onCorrect={requestCorrection} />
      <Dialog open={!!claimAsk} onOpenChange={(v) => !v && answerClaim(false)}>
        <DialogContent data-testid="claim-anon-dialog">
          <DialogHeader><DialogTitle>Add your earlier conversation?</DialogTitle>
            <DialogDescription>We found an Ask Nauticus conversation on this device from before you signed in. Add it (and any plan you picked) to this account?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => answerClaim(false)} className="h-12 rounded-full" data-testid="claim-anon-no">No, don't add it</Button>
            <Button onClick={() => answerClaim(true)} className="h-12 rounded-full bg-[#0B192C] text-white" data-testid="claim-anon-yes">Yes, add it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={finderOpen} onOpenChange={(o) => { setFinderOpen(o); if (!o) setFinderSeed(null); }}>
        <DialogContent mobileFull className="max-w-5xl w-full sm:w-full h-[100dvh] sm:h-auto max-h-[100dvh] sm:max-h-[90vh] rounded-none sm:rounded-lg overflow-y-auto overflow-x-hidden p-4 sm:p-6 bg-[#F7F6F2]" data-testid="member-finder-dialog">
          <DialogHeader><DialogTitle>Find your best-fit 2027 plans</DialogTitle>
            <DialogDescription>Tell us your ZIP, doctors, medicines, conditions, Medicaid and what matters most. Your case is saved to your household.</DialogDescription>
          </DialogHeader>
          {finderOpen && <DecisionCase base="/consumer/case" variant="consumer" seed={finderSeed} contact={line} onOpenLegacy={() => { setFinderOpen(false); setCompareOpen(true); }} onRequestAdvisor={() => { setFinderOpen(false); setAdvisorReason("Doctor and prescription review"); setAdvisorOpen(true); }} />}
        </DialogContent>
      </Dialog>
      <AdvisorDialog open={advisorOpen} onOpenChange={(o) => { setAdvisorOpen(o); if (!o) setAdvisorReason(null); }} token={token} agent={ctx?.agent} reason={advisorReason} />
      <Dialog open={compareOpen} onOpenChange={(o) => { setCompareOpen(o); if (!o) setCompareEligible(false); }}>
        <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[88vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="consumer-compare-dialog">
          <DialogHeader><DialogTitle>{compareEligible ? "Compare 2027 plans you qualify for" : "Compare 2027 Medicare plans"}</DialogTitle>
            <DialogDescription>{compareEligible ? "These are the 2027 plans you qualify for based on what you've told us, shown next to your current coverage. Add your doctors and medications below the plans to check them plan by plan." : "Pick plans by carrier in your county and see supported 2027 facts next to your current coverage. Add your doctors and medications below the plans to check them plan by plan."}</DialogDescription>
          </DialogHeader>
          <PlanComparePanel variant="member" token={token}
            onRequestAdvisor={(reason) => { setCompareOpen(false); setAdvisorReason(typeof reason === "string" ? reason : null); setAdvisorOpen(true); }}
            onSelfEnroll={ctx?.self_enroll_available ? handleSelfEnroll : undefined} onSaveCompareAdvisor={handleSaveCompareAdvisor}
            defaultCounty={ctx?.residence?.county || ""} defaultState={ctx?.residence?.state || "FL"}
            eligibilityPrefill={compareEligible ? eligibilityPrefill : null}
            initialCurrent={confirmedCurrentCard || (ctx?.compare_current_identity ? { contract_id: ctx.compare_current_identity.contract_id, pbp: ctx.compare_current_identity.pbp, segment_id: ctx.compare_current_identity.segment_id, display_id: `${ctx.compare_current_identity.contract_id}-${ctx.compare_current_identity.pbp}-${ctx.compare_current_identity.segment_id}`, plan_name: ctx.compare_current_identity.plan_name || `${ctx.compare_current_identity.contract_id}-${ctx.compare_current_identity.pbp}-${ctx.compare_current_identity.segment_id}` } : null)}
            initialCandidates={(ctx?.candidate_plans || []).map((c) => ({ contract_id: c.contract_id, pbp: c.pbp, segment_id: c.segment_id, display_id: c.display_id, plan_name: c.plan_name, organization: c.carrier, plan_type: c.plan_type, snp_type: c.snp_type }))} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReplaceCurrentDialog({ ask, onReplace, onKeep, onCorrect }) {
  if (!ask) return null;
  if (ask.locked) return (
    <Dialog open onOpenChange={(v) => !v && onKeep()}>
      <DialogContent data-testid="correct-current-dialog">
        <DialogHeader><DialogTitle>Your licensed insurance agent has your plan on file</DialogTitle>
          <DialogDescription>
            Your licensed insurance agent recorded <b>{ask.existing?.plan_name || "a plan"}</b> as your current plan, so it can't be changed here.
            If it's wrong, we can ask your licensed insurance agent to correct it to <b>{ask.pl?.plan_name}</b>.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onKeep} className="h-12 rounded-full" data-testid="correct-current-cancel">Leave it as is</Button>
          <Button onClick={onCorrect} className="h-12 rounded-full bg-[#0B192C] text-white" data-testid="correct-current-request">Ask my agent to correct it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return (
    <Dialog open onOpenChange={(v) => !v && onKeep()}>
      <DialogContent data-testid="replace-current-dialog">
        <DialogHeader><DialogTitle>Change your current plan?</DialogTitle>
          <DialogDescription>
            You already have <b data-testid="replace-current-existing">{ask.existing?.plan_name || "a plan"}</b> saved as your current plan.
            Replace it with <b data-testid="replace-current-new">{ask.pl?.plan_name}</b>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onKeep} className="h-12 rounded-full" data-testid="replace-current-keep">Keep my saved plan</Button>
          <Button onClick={onReplace} className="h-12 rounded-full bg-[#0B192C] text-white" data-testid="replace-current-confirm">Yes, replace it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecapDialog({ open, onOpenChange, recap, onAdvisor }) {
  if (!recap) return null;
  const Section = ({ icon: Icon, title, items, empty }) => (
    <div>
      <p className="text-xs font-bold text-[#0B192C] uppercase tracking-wide flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5" />{title}</p>
      {items?.length ? <ul className="text-sm text-[#374151] list-disc pl-5 space-y-0.5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
        : <p className="text-sm text-[#9CA3AF]">{empty}</p>}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="consumer-recap-dialog">
        <DialogHeader><DialogTitle>Here's what we covered</DialogTitle>
          <DialogDescription>A recap built only from what we discussed and the facts you've confirmed in My Nauticus.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Section icon={CheckCircle2} title="We confirmed" items={recap.confirmed} empty="Nothing confirmed yet." />
          <Section icon={HelpCircle} title="Still to verify" items={recap.not_verified} empty="Nothing outstanding." />
          <Section icon={ShieldCheck} title="Your My Nauticus details" items={recap.household} empty="—" />
          <Section icon={Sparkles} title="Worth completing" items={recap.still_worth_completing} empty="You're all set for now." />
          {recap.nothing_required && <p className="text-sm text-[#065F46] bg-[#D1FAE5] rounded-lg px-3 py-2">Based on what I can verify, there's nothing you need to do right now.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={onAdvisor} className="bg-[#0B192C] text-white" data-testid="recap-advisor">Talk to my agent</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdvisorDialog({ open, onOpenChange, token, agent, reason }) {
  const who = agent?.name ? `your licensed insurance agent, ${agent.name}${agent.agency ? ` (${agent.agency})` : ""}` : "a licensed insurance agent";
  const [form, setForm] = useState({ phone: "", email: "", consent: false, shareTranscript: false });
  const [busy, setBusy] = useState(false);
  const known = useContactPrefill(open, token ? `/consumer/ask/${token}/contact-prefill` : null, setForm);
  const consentOnFile = !!known?.contact_consent?.on_file;
  const SHARE_TEXT = "I agree to share my full Ask Nauticus conversation with Senior Benefit Advisors FL so my licensed insurance agent has complete context.";
  const submit = async () => {
    if (!form.phone && !form.email) { toast.error("Add a phone or email."); return; }
    if (!form.consent && !consentOnFile) { toast.error("Please give consent to be contacted."); return; }
    setBusy(true);
    try {
      await api.post(`/consumer/ask/${token}/handoff`, {
        phone: form.phone || null, email: form.email || null, consent: !!form.consent,
        share_transcript: form.shareTranscript, share_transcript_text: SHARE_TEXT, request_reason: reason || null });
      toast.success(`Done — ${who} will follow up with your conversation in hand.`);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="consumer-advisor-dialog">
        <DialogHeader><DialogTitle data-testid="consumer-advisor-dialog-title">Talk to {agent?.name ? "your licensed insurance agent" : "a licensed insurance agent"}</DialogTitle>
          <DialogDescription>Share a way to reach you and your consent; a short summary of your conversation is passed along for context.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground" data-testid="consumer-advisor-who">{who.charAt(0).toUpperCase() + who.slice(1)} may contact you about this request. A short summary of your conversation is shared for context so you don't repeat yourself.</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="consumer-advisor-phone" />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="consumer-advisor-email" />
        </div>
        {consentOnFile ? <ConsentOnFile testid="consumer-advisor-consent-on-file" /> : (
        <label className="flex items-start gap-2 text-sm pt-1">
          <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="consumer-advisor-consent" />
          <span>A licensed insurance agent may contact me about this request.</span>
        </label>)}
        <label className="flex items-start gap-2 text-sm rounded-lg border border-[#E5E5E0] bg-[#FBFBF8] p-3">
          <Checkbox checked={form.shareTranscript} onCheckedChange={(v) => setForm({ ...form, shareTranscript: !!v })} data-testid="consumer-advisor-share-transcript" />
          <span>{SHARE_TEXT} <span className="text-muted-foreground">(Optional — without this, only a short summary is shared.)</span></span>
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0B192C] text-white" data-testid="consumer-advisor-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request an agent"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
